import React, { useEffect } from 'react';

const DeleteAccount = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <section className="min-h-screen bg-background-dark pt-32 pb-20 px-4 md:px-10">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-white text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            Delete Account & Data
          </h1>
          <p className="text-text-muted text-lg mb-8">You're always in control of your data.</p>
          
          {/* GDPR Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-dark border border-border-dark text-text-muted text-xs font-semibold mb-6">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
            </svg>
            GDPR & Privacy Compliant
          </div>
        </div>

        {/* Privacy Commitment Box */}
        <div className="bg-surface-dark border border-primary/30 rounded-2xl p-6 md:p-8 mb-12 flex items-start gap-4">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-white text-lg font-bold mb-2">Your Privacy Commitment</h3>
            <p className="text-text-muted text-sm leading-relaxed">
              UPI Trackr does not store your data on external servers. Account deletion immediately removes all locally stored data from your device. If you enabled Google Drive backups, those files are also deleted upon confirmation.
            </p>
          </div>
        </div>

        {/* How to Delete Account Section */}
        <div className="mb-16">
          <h2 className="text-white text-2xl md:text-3xl font-bold mb-8">How to delete your account</h2>
          
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex gap-4 md:gap-6">
              <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-background-dark font-bold text-sm">
                  1
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold mb-2">Open the UPI Trackr App</h3>
                <p className="text-text-muted text-sm">Launch the application on your mobile device.</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4 md:gap-6">
              <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-background-dark font-bold text-sm">
                  2
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold mb-2">Go to Settings</h3>
                <p className="text-text-muted text-sm">Tap on the profile or settings icon in the top navigation bar.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4 md:gap-6">
              <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-background-dark font-bold text-sm">
                  3
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold mb-2">Select "Delete Account / Delete All Data"</h3>
                <p className="text-text-muted text-sm">Scroll down to the Danger Zone or Data Management section.</p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4 md:gap-6">
              <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-background-dark font-bold text-sm">
                  4
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold mb-2">Confirm Deletion</h3>
                <p className="text-text-muted text-sm">Confirm your choice. This action is irreversible.</p>
              </div>
            </div>
          </div>
        </div>

        {/* What Data is Deleted Section */}
        <div className="mb-16">
          <h2 className="text-white text-2xl md:text-3xl font-bold mb-8">What data is deleted?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card 1 */}
            <div className="bg-surface-dark border border-border-dark rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5.04-6.71l-2.75 3.54-2.16-2.66c-.44-.53-1.25-.58-1.78-.15-.53.44-.58 1.25-.15 1.78l3 3.68c.25.31.61.5 1.00.5.39 0 .75-.19 1.00-.5l4-5.15c.44-.53.39-1.34-.15-1.78-.53-.43-1.34-.38-1.78.15z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold">Google Account Link</h3>
              </div>
              <p className="text-text-muted text-sm">The link to your Google profile used for sign in is severed.</p>
            </div>

            {/* Card 2 */}
            <div className="bg-surface-dark border border-border-dark rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-4.42 3.58-8 8-8s8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold">Transaction Data</h3>
              </div>
              <p className="text-text-muted text-sm">All imported PDF statements and parsed transactions are wiped.</p>
            </div>

            {/* Card 3 */}
            <div className="bg-surface-dark border border-border-dark rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 9.5c0 .83-.67 1.5-1.5 1.5S11 13.33 11 12.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold">Analytics Summaries</h3>
              </div>
              <p className="text-text-muted text-sm">All generated charts, spending patterns, and summaries are deleted.</p>
            </div>

            {/* Card 4 */}
            <div className="bg-surface-dark border border-border-dark rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold">Cloud Backups</h3>
              </div>
              <p className="text-text-muted text-sm">If Google Drive sync was active, backup files are removed.</p>
            </div>
          </div>
        </div>

        {/* Important Transparency Notes */}
        <div className="bg-surface-dark border border-border-dark rounded-2xl p-6 md:p-8 mb-12">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 mt-1">
              <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <h3 className="text-white text-lg font-bold">Important Transparency Notes</h3>
          </div>
          
          <ul className="space-y-4 text-text-muted text-sm">
            <li className="flex gap-3">
              <span className="text-primary font-bold mt-0.5">•</span>
              <span>
                <strong>Since UPI Trackr operates offline first, no personal data is ever retained on our company servers</strong> because we never collected it in the first place.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary font-bold mt-0.5">•</span>
              <span>
                Uninstalling the app from your phone <strong>does not automatically delete</strong> Google Drive backups. Please use the in-app "Delete Account" function to ensure cloud backups are also removed.
              </span>
            </li>
          </ul>
        </div>

        {/* Support Section */}
        <div className="text-center">
          <p className="text-text-muted text-sm mb-4">If you're unable to access the app or have trouble deleting your data:</p>
          <a 
            href="mailto:itsankar2005@gmail.com"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary text-primary hover:bg-primary/20 transition-colors text-sm font-semibold"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
            Contact Support: itsankar2005@gmail.com
          </a>
        </div>
      </div>
    </section>
  );
};

export default DeleteAccount;
